import { Link, useRouterState } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { queryClient } from "@/trpc/client";
import { workspacesQueryOptions } from "@/lib/workspaces";
import { isSignupProvisioning } from "@/lib/signupProvisioning";
import type { MouseEventHandler } from "react";

/** Said in one place: the region below and any future copy must not drift apart. */
const PENDING_COPY = "Opening the dashboard…";

/**
 * The signed-in "Dashboard" CTA, in ONE place — `StartFreeCta` and `_home`'s
 * `Nav` both rendered their own copy of the same `<Link>`, which is how a fix
 * applied to one silently misses the other.
 *
 * WHY IT NEEDS A PENDING STATE AT ALL. `/dashboard/`'s `beforeLoad` awaits
 * `getEntryWorkspace()` before it can `redirect`. The URL has ALREADY changed
 * by then — `commitLocation` pushes history synchronously and only then runs
 * `load()` — but the matches have not committed, so the marketing page stays
 * on screen, unchanged, for a whole network round-trip. The address bar says
 * one thing and the page says another, and nothing indicates work is underway.
 *
 * SCOPED TO THE DESTINATION, not to `isLoading` alone. This header is mounted
 * across every `_home` navigation, so a bare `state.isLoading` would spin the
 * Dashboard button while the reader clicked "About".
 *
 * THE LABEL DOES NOT CHANGE — the spinner joins it. Swapping "Dashboard" for
 * "Opening…" was tried and reverted: this link sits in a flex nav beside Blog
 * and Docs, and either direction of width change shoves that row sideways at
 * the moment the reader is watching it (the swapped copy is in fact the wider
 * of the two). A spinner appearing next to a stable label is also the shape
 * `AiAutofillButton` already uses for the same job.
 *
 * Still a `<Link>`, not a `<button>` with `navigate()`: middle-click,
 * right-click and "copy link address" all have to keep working.
 */
export function DashboardLink({
  announce = false,
  onClick,
}: {
  announce?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}) {
  const pending = useRouterState({
    select: (state) =>
      state.isLoading && state.location.pathname.startsWith("/dashboard"),
  });

  /**
   * Warm the query the destination guard reads, so the click has nothing to
   * wait for. `getEntryWorkspace` reads this exact options object through
   * `fetchQuery`, whose `staleTime` is 60s for a NON-EMPTY list and 0 for an
   * empty one (`workspaces.ts` — an empty read must never be pinned). For the
   * audience this renders to the list is non-empty, so the guard resolves from
   * cache with no network hop; a workspace-less user re-reads, which is the
   * intended behaviour and cheap.
   *
   * NEVER MID-SIGNUP. This is the only read of this key that does not go
   * through `awaitSignupProvisioning()`, and skipping the barrier is not
   * harmless: a `getWorkspaces` taken inside the provisioning window returns
   * `[]`, and the empty-list `staleTime: 0` rule does NOT save us, because it
   * only re-reads a SETTLED entry. `fetchQuery` joins an in-flight one instead
   * (no `cancelRefetch`), so `login.tsx`'s post-signup `getEntryWorkspace()`
   * would adopt this prefetch's `[]` and bounce a brand-new account to `/` —
   * the exact bug `signupProvisioning.ts` exists to prevent.
   *
   * `prefetchQuery`, not `fetchQuery`/`ensureQueryData`: it is
   * `fetchQuery(...).then(noop).catch(noop)` and so never rejects. In a
   * fire-and-forget handler either of the others would raise an unhandled
   * rejection every time a hover request failed.
   */
  const warm = () => {
    if (isSignupProvisioning()) return;
    void queryClient.prefetchQuery(workspacesQueryOptions);
  };

  return (
    <>
      <Link
        to="/dashboard"
        data-variant="primary"
        aria-busy={pending}
        onClick={onClick}
        onMouseEnter={warm}
        onFocus={warm}
      >
        {pending && <Loader2 className="size-4 animate-spin" aria-hidden />}
        Dashboard
      </Link>
      {/*
        The announcement, and the ONLY one: the label does not change, so a
        screen-reader user gets nothing from the link itself, and `aria-busy`
        on an element they are not focused on announces nothing either.

        OPT-IN, because three of these are mounted at once on the signed-in
        landing page — `Hero` and `FinalCta` (both via `StartFreeCta`) and the
        `Nav`. They all read the same router state and flip together, so an
        unconditional region would say the same sentence three times in one
        tick. `Nav` sets `announce`; it is the instance present on every
        `_home` route, and a live region announces regardless of which of the
        three the reader actually clicked.

        Mounted at all times and EMPTY until there is something to say — the
        shape `AiAutofillButton`'s in-flight region uses, and for the same
        reason: a live region that enters the DOM together with its content is
        a new node, not a change to a watched one, and is announced by
        essentially nothing. `sr-only` is `position: absolute`, so an idle
        region is not a flex item and costs the nav no gap.
      */}
      {announce && (
        <span role="status" className="sr-only">
          {pending ? PENDING_COPY : null}
        </span>
      )}
    </>
  );
}
